<template>
  <div class="post-detail-container">
    <!-- 顶部导航栏 -->
    <NavBar
      title=""
      :show-more="true"
      :show-delete="threadAuthorId === userStore.userInfo?.uid"
      :show-language="true"
    />
    <!-- 加载中 -->
    <div v-if="loading" class="loading-wrap">
      <div class="spinner"></div>
    </div>
    <template v-else>
      <!-- 内容区 -->
      <div class="detail-wrap">
        <!-- 用户信息 -->
        <div class="user-info-wrap">
          <Avatar
            class="avatar"
            :avatar-index="threadStore.formattedThreadDetail?.avatar"
            size="medium"
            :clickable="true"
            :enable-navigate="true"
            :user-id="threadStore.formattedThreadDetail?.uid || ''"
            :avatar-frame="threadStore.formattedThreadDetail?.avatar_frame"
          />
          <div class="user-meta">
            <div class="nickname-wrapper">
              <div class="nickname">
                {{ threadStore.formattedThreadDetail?.username }}
              </div>
              <!-- 用户称号展示 -->
              <div
                v-if="
                  threadStore.formattedThreadDetail?.user_title &&
                  threadStore.formattedThreadDetail?.user_title.length > 0
                "
                class="post-titles"
              >
                <span
                  v-for="(title, index) in threadStore.formattedThreadDetail
                    ?.user_title"
                  :key="index"
                  class="post-title-badge"
                >
                  {{ title }}
                </span>
              </div>
            </div>
            <div class="date-wrapper">
              <div class="date">
                {{ formatDateline(threadStore.formattedThreadDetail?.date, t) }}
              </div>
              <div
                v-if="threadStore.formattedThreadDetail?.is_translated === 1"
                class="translated-tip"
              >
                {{ t("postDetail.translatedTo") }}
                {{ languageStore.currentLanguageName }}
              </div>
            </div>
          </div>
          <button
            class="follow-btn"
            :class="
              threadStore.formattedThreadDetail?.isFollowed
                ? 'followed'
                : 'unfollowed'
            "
            v-if="
              threadStore.formattedThreadDetail?.uid !== userStore.userInfo?.uid
            "
            @click="toggleFollow"
          >
            {{
              threadStore.formattedThreadDetail?.isFollowed
                ? t("profile.followed")
                : "+ " + t("profile.follow")
            }}
          </button>
        </div>

        <!-- 标题和正文 -->
        <div class="post-title">
          {{ threadStore.formattedThreadDetail?.title }}
        </div>
        <div
          class="post-content"
          v-html="decodeHtml(threadStore.formattedThreadDetail?.content)"
          @click="handleImageClick"
        ></div>

        <!-- 标签展示区域 -->
        <TagList
          :tags="threadStore.formattedThreadDetail?.tag_info || []"
          mode="display"
        />

        <!-- 帖子图片 -->
        <!-- <div
          v-if="
            threadStore.formattedThreadDetail?.images &&
            threadStore.formattedThreadDetail?.images.length
          "
          class="post-images"
        >
          <img
            v-for="(img, idx) in threadStore.formattedThreadDetail?.images"
            :key="idx"
            :src="img"
            class="post-image"
          />
        </div> -->
      </div>

      <!-- 回复列表板块 -->
      <ReplyList
        :author-username="threadStore.formattedThreadDetail?.username"
      />

      <!-- 底部评论与互动栏 -->
      <PostBottomBar
        :post="threadStore.formattedThreadDetail"
        @comment-click="handleCommentClick"
        @update:post="handlePostUpdate"
      />
    </template>

    <!-- 回复帖子时，显示评论框 -->
    <CommentModal
      v-model="showComment"
      :reply-to="threadStore.formattedThreadDetail?.username"
      @submit="handleCommentSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onActivated, computed, onUnmounted, nextTick, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { useThreadStore } from "@/stores/thread";
import { useFavoriteStore } from "@/stores/favorite";
import { useUserStore } from "@/stores";
import { useLikeStore } from "@/stores/like";
// @ts-ignore
import NavBar from "@/components/common/NavBar.vue";
// @ts-ignore
import Avatar from "@/components/common/Avatar.vue";
// @ts-ignore
import CommentModal from "@/components/postDetail/CommentModal.vue";
// @ts-ignore
import ReplyList from "@/components/postDetail/ReplyList.vue";
// @ts-ignore
import PostBottomBar from "@/components/postDetail/PostBottomBar.vue";
import {
  formatDateline,
  decodeHtml,
  extractImageLinks,
  getDeviceLanguage,
} from "@/utils";
import { useToastStore, useImageViewerStore, useLanguageStore } from "@/stores";
// @ts-ignore
import { debounce } from "lodash-es";
import { clickButton, pageType } from "@/utils/point";
import { ContentEventDelegation } from "@/utils/contentEventDelegation";
// @ts-ignore
import TagList from "@/components/common/TagList.vue";
const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();
const threadStore = useThreadStore();
const favoriteStore = useFavoriteStore();
const userStore = useUserStore();
const likeStore = useLikeStore();
const loading = ref(true);
const toast = useToastStore();
const imageViewer = useImageViewerStore();
const languageStore = useLanguageStore();

const showComment = ref(false);

// 关注、收藏、点赞状态本地可维护一份，为演示前端交互（实际应调后端接口后刷新）
const localStarred = ref(false);
const localLiked = ref(false);

// 图片预览相关
const handleImageClick = (event: Event) => {
  const target = event.target as HTMLImageElement;
  if (target.tagName === "IMG") {
    const content = threadStore.formattedThreadDetail?.content || "";
    const imageLinks = extractImageLinks(content);

    if (imageLinks.length > 0) {
      // 找到被点击的图片在数组中的索引
      const clickedSrc = target.src;
      const imageIndex = imageLinks.findIndex((link) => link === clickedSrc);

      if (imageIndex !== -1) {
        imageViewer.show(imageLinks, imageIndex);
      }
    }
  }
};
const threadAuthorId = computed(() => {
  return threadStore.formattedThreadDetail?.uid;
});
const toggleFollow = async () => {
  const isFollowed = threadStore.formattedThreadDetail?.isFollowed;
  if (!threadAuthorId.value) return;

  if (isFollowed) {
    const res = await userStore.unfollow(threadAuthorId.value);
    if (res?.code == 200) {
      // 更新本地状态
      threadStore.updateFollowStatus(threadAuthorId.value, false);
      toast.showToast(t("profile.unfollowSuccess"), "success", 1000);
    }
  } else {
    const res = await userStore.follow(threadAuthorId.value);
    if (res?.code == 200) {
      // 更新本地状态
      threadStore.updateFollowStatus(threadAuthorId.value, true);
      toast.showToast(t("profile.followSuccess"), "success", 1000);
    }
  }
};

const handleCommentClick = () => {
  clickButton({
    eventType: pageType.POST_DETAIL,
    eventReason: "评论",
    eventValue: JSON.stringify({
      tid: threadStore.currentThread?.tid,
      uid: userStore.userInfo?.uid,
    }),
  });
  showComment.value = true;
};
const handleCommentSubmit = async (content: string) => {
  const threadId = threadStore.currentThread?.tid;
  if (!threadId) return;

  try {
    // 提示发送中
    toast.showToast(t("postDetail.sending"), "loading", 10000);
    const res = await threadStore.createReply(
      threadId,
      content,
      getDeviceLanguage()
    );
    if (res.code != 200) {
      toast.showErrorCodeToast(+res.code, t);
      return;
    }
    clickButton({
      eventType: pageType.POST_DETAIL,
      eventReason: "提交评论",
      eventValue: JSON.stringify({
        tid: threadId,
        uid: userStore.userInfo?.uid,
      }),
    });
    showComment.value = false;
    toast.showToast(t("postDetail.sendSuccess"), "success", 1000);
    // 重置帖子选项
    threadStore.resetThreadDetail();
    // 重新获取帖子详情
    await threadStore.getThreadDetail(threadId);
  } catch (error) {
    toast.showToast(t("postDetail.sendFailed"), "error", 2000);
  }
};

const handlePostUpdate = (updatedPost: any) => {
  // 当 PostBottomBar 更新帖子详情时，更新 store 中的数据
  if (updatedPost && threadStore.currentThread) {
    // 直接更新 currentThread 中的相关字段
    if (updatedPost.isLiked !== undefined) {
      threadStore.currentThread.is_liked = updatedPost.isLiked;
      threadStore.currentThread.likes = updatedPost.likes;
    }
    if (updatedPost.isStarred !== undefined) {
      threadStore.currentThread.is_favorited = updatedPost.isStarred;
      threadStore.currentThread.favtimes = updatedPost.stars;
    }
  }
};

const initPostDetail = async () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
  loading.value = true;

  try {
    const threadId = Number(route.params.id);
    if (!threadId) {
      throw new Error("无效的帖子ID");
    }
    const res = await threadStore.getThreadDetail(threadId);
    if (res.code != 200) {
      toast.showErrorCodeToast(+res.code, t);
    }
    // 使用 store 中的数据，不再赋值给 post
  } catch (error) {
    console.error("获取帖子详情失败:", error);
    // 可以在这里添加错误处理，比如显示错误提示
  } finally {
    loading.value = false;
  }
};

// 事件委托：处理正文内容中的 mention-user 和 post-link 点击
const contentEventDelegation = new ContentEventDelegation(
  ".post-detail-container",
  router
);

// 初始化事件绑定
const initContentEvents = () => {
  nextTick(() => {
    contentEventDelegation.init();
  });
};

// 防抖处理语言切换时的帖子详情请求，避免快速切换语言时的竞态条件
// 300ms 延迟，确保只执行最后一次语言切换
const debouncedInitPostDetail = debounce(() => {
  const threadId = Number(route.params.id);
  if (threadId) {
    initPostDetail();
  }
}, 300);

onUnmounted(() => {
  contentEventDelegation.cleanup();
  // 取消防抖，避免内存泄漏
  debouncedInitPostDetail.cancel();
});

onActivated(() => {
  initPostDetail();
  threadStore.resetThreadDetail();
  // 重新初始化事件绑定（因为内容可能已更新）
  initContentEvents();
});

// 监听语言变化，重新请求帖子详情
watch(
  () => languageStore.curLanguage,
  () => {
    // 当语言变化时，使用防抖函数重新获取帖子详情
    debouncedInitPostDetail();
  }
);
</script>

<style scoped lang="scss">
$primary-color: #dd8632;

.post-detail-container {
  position: relative;
  background: #fff;
  min-height: 100vh;
}

.detail-wrap {
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 0 10px;
}

.navbar {
  height: 50px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 18px;
  font-weight: 600;
  color: #222;
  background: #fff;
  position: fixed;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  top: 0;
  z-index: 10;
  padding: 0 16px;

  .nav-icon {
    width: 24px;
    height: 24px;
    cursor: pointer;
  }

  .navbar-title {
    flex: 1;
    text-align: center;
  }
  .back-icon {
    height: 18px;
    object-fit: contain;
  }
}

.loading-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f7f7f7;
    border-top: 4px solid $primary-color;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.user-info-wrap {
  display: flex;
  align-items: center;
  padding: 18px 16px 8px 16px;
  .avatar {
    margin-right: 12px;
    cursor: pointer;
  }
  .user-meta {
    flex: 1;
    display: flex;
    flex-direction: column;
    .nickname-wrapper {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 2px;
    }
    .nickname {
      font-size: 16px;
      font-weight: 600;
      color: #222;
    }
    .post-titles {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;
    }
    .post-title-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      font-size: 10px;
      font-weight: 500;
      color: #fff;
      background: linear-gradient(135deg, #ffb400 0%, #ff9500 100%);
      border-radius: 12px;
      box-shadow: 0 2px 6px rgba(255, 180, 0, 0.25);
      white-space: nowrap;
      line-height: 1.2;
      transition: all 0.2s ease;
      position: relative;
      overflow: hidden;
    }
    .date-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 2px;
    }
    .date {
      font-size: 12px;
      color: #bbb;
    }
    .translated-tip {
      font-size: 12px;
      color: #999;
    }
  }
  .follow-btn {
    margin-left: auto;
    padding: 2px 10px;
    border: 1px solid $primary-color;
    background: transparent;
    color: $primary-color;
    border-radius: 20px;
    font-size: 14px;
    outline: none;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    cursor: pointer;

    &.followed {
      background: #f5f5f5;
      color: #999;
      border: 1px solid #ddd;
    }

    &.unfollowed:hover {
      background: $primary-color;
      color: #fff;
    }
  }
}

.post-title {
  font-size: 18px;
  font-weight: 700;
  color: #222;
  margin: 10px 16px 6px 16px;
}
// 帖子内容
.post-content {
  font-size: 14px;
  color: #333;
  margin: 0 16px 20px 16px;
  line-height: 1.8;
  word-break: break-word;
  :deep(p) {
    margin: 0 0 12px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(h1) {
    font-size: 20px;
    margin: 20px 0 12px 0;
    color: #333;
  }

  :deep(h2) {
    font-size: 18px;
    margin: 18px 0 10px 0;
    color: #333;
    line-height: 1.4;
  }

  :deep(h3) {
    font-size: 16px;
    margin: 16px 0 10px 0;
    color: #333;
    line-height: 1.4;
  }

  :deep(h4) {
    font-size: 14px;
    margin: 14px 0 8px 0;
    color: #333;
    line-height: 1.4;
  }

  :deep(h5) {
    font-size: 13px;
    margin: 12px 0 8px 0;
    color: #333;
    line-height: 1.4;
  }

  :deep(h6) {
    font-size: 12px;
    margin: 10px 0 6px 0;
    color: #333;
    line-height: 1.4;
  }

  :deep(pre) {
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.5;
    color: #333;
  }

  :deep(img) {
    max-width: 100%;
    height: unset !important;
    cursor: pointer;
  }

  :deep(table) {
    max-width: 100vw !important;
    width: 100% !important;
    height: auto !important;
    overflow-x: auto !important;
    display: block !important;
    table-layout: fixed !important;

    // 确保表格内容可以水平滚动
    tbody,
    thead,
    tfoot {
      display: table !important;
      width: 100% !important;
      table-layout: fixed !important;
    }

    // 处理表格单元格
    td,
    th {
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      min-width: 60px !important;
      max-width: 100vw !important;
    }
  }
}

.tag-list-wrapper {
  margin: 10px 16px;
}

.post-images {
  margin: 0 16px 12px 16px;
  .post-image {
    width: 100%;
    border-radius: 12px;
    margin-top: 6px;
    object-fit: cover;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
}
</style>
